
import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter as Router, useLocation } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { PremiumDialogProvider } from "./contexts/PremiumDialogContext";
import { NotificationsProvider } from "./contexts/NotificationsContext";
 
import { EnhancedNavigation } from "./components/navigation/EnhancedNavigation";
import { AppRoutes } from "./components/routing/AppRoutes";
import { LoadingFallback } from "./components/common/LoadingFallback";
import { useAuth } from "./contexts/AuthContext";
import { PremiumDialog } from "./components/premium/PremiumDialog";
import { NotificationsPortal } from "./components/notifications/NotificationsPortal";
import ErrorManagementService from "./services/error-management";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { useAutomaticEmbeddings } from "./hooks/use-automatic-embeddings";
import { useAutoCommunityInitialization } from "./hooks/use-auto-community-initialization";
import PostFlowOverlay from "@/components/posting/PostFlowOverlay";
import { DomainRedirect } from "./components/routing/DomainRedirect";
import { VerifiedWelcomeModal } from "./components/verification/VerifiedWelcomeModal";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if ((error as any)?.nonRetryable) return false;
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 1000 * 60 * 2, // Increased to 2 minutes to reduce race conditions
      gcTime: 1000 * 60 * 10, // Increased to 10 minutes for better caching
      refetchOnWindowFocus: false,
      // Add query deduplication
      queryKeyHashFn: (queryKey) => JSON.stringify(queryKey),
      networkMode: 'always', // Ensure queries work in all network conditions
    },
    mutations: {
      retry: false,
      networkMode: 'always',
    }
  }
});

// Add global error handler
queryClient.setDefaultOptions({
  queries: {
    meta: {
      onError: (error: unknown) => {
        ErrorManagementService.handleQueryError(error);
      }
    }
  },
  mutations: {
    meta: {
      onError: (error: unknown) => {
        ErrorManagementService.handleQueryError(error);
      }
    }
  }
});

// Scroll restoration on route change
const ScrollToTop: React.FC = () => {
  const location = useLocation();
  React.useEffect(() => {
    // Scroll window
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    } catch {
      // no-op if environment doesn't support options
      window.scrollTo(0, 0);
    }
    // Scroll internal content container if used
    const el = document.getElementById('app-scroll-container');
    if (el) {
      el.scrollTop = 0;
    }
  }, [location.pathname]);
  return null;
};

// App content that uses both AuthContext and MaintenanceContext
const AppContent = () => {
  const { isInitialized } = useAuth();
  const location = useLocation();  
  const isSplineLanding = location.pathname === '/spline-landing';
  // Global Posting overlay state
  const [postOverlayOpen, setPostOverlayOpen] = React.useState(false);
  const [overlayMode, setOverlayMode] = React.useState<'full' | 'sheet'>('full');
  
  // No longer hiding navigation on these pages
  // Keep this as a comment for reference of which pages use GlobalHeader
  // const usesGlobalHeader = [
  //   '/bulletins',
  //   '/loops',
  //   '/top-follows', 
  //   '/canvas-test',
  //   '/profile',
  //   '/user/',
  //   '/2/', // BentoProfile
  //   '/settings',
  //   '/drops'
  // ].some(path => location.pathname.startsWith(path));
  
  // Using enhanced navigation which combines modern Navigation and necessary MainNav features
  const usesGlobalHeader = false;
  
  // Automatically run embeddings backfill when needed
  useAutomaticEmbeddings();
  
  // Automatically initialize communities when needed
  useAutoCommunityInitialization();
  // Listen for global post flow events so any composer can trigger it
  React.useEffect(() => {
    const openHandler = (e: Event) => {
      const ce = e as CustomEvent<any>;
      const mode = ce.detail?.mode as 'full' | 'sheet' | undefined;
      setOverlayMode(mode || 'full');
      setPostOverlayOpen(true);
      // Optionally allow initializing step states via detail.init
      if (Array.isArray(ce.detail?.init)) {
        window.dispatchEvent(new CustomEvent('post-flow:update', { detail: ce.detail.init }));
      }
    };
    const closeHandler = () => setPostOverlayOpen(false);
    window.addEventListener('post-flow:open', openHandler as EventListener);
    window.addEventListener('post-flow:close', closeHandler);
    return () => {
      window.removeEventListener('post-flow:open', openHandler as EventListener);
      window.removeEventListener('post-flow:close', closeHandler);
    };
  }, []);
  
  console.log("AppContent - State:", { 
    isInitialized,
    timestamp: new Date().toISOString() 
  });

  if (!isInitialized) {
    console.log("AppContent - Waiting for initialization");
    return <LoadingFallback />;
  }

  console.log("AppContent - Rendering main content");
  return (
    <div className={isSplineLanding ? "overflow-hidden" : ""}>
      {/* Ensure each navigation starts at the top */}
      <ScrollToTop />
      {!isSplineLanding ? (
        <EnhancedNavigation>
          <AppRoutes />
          <PremiumDialog />
          <NotificationsPortal />
          <VerifiedWelcomeModal />
          {/* Global Posting overlay */}
          <PostFlowOverlay
            open={postOverlayOpen}
            mode={overlayMode}
            onMinimize={() => setOverlayMode('sheet')}
            onClose={() => setPostOverlayOpen(false)}
          />
        </EnhancedNavigation>
      ) : (
        <>
          <AppRoutes />
          <PremiumDialog />
          <NotificationsPortal />
          <VerifiedWelcomeModal />
          <PostFlowOverlay
            open={postOverlayOpen}
            mode={overlayMode}
            onMinimize={() => setOverlayMode('sheet')}
            onClose={() => setPostOverlayOpen(false)}
          />
        </>
      )}
    </div>
  );
};

const App: React.FC = () => {
  console.log("App - Initial render");
  
  return (
    <Router>
      <DomainRedirect />
      <QueryClientProvider client={queryClient}>
        <TooltipPrimitive.Provider delayDuration={200}>
          <TooltipProvider>
            <AuthProvider>
              <NotificationsProvider>
                <PremiumDialogProvider>
                  <AppContent />
                  <Toaster />
                  <Sonner />
                </PremiumDialogProvider>
              </NotificationsProvider>
            </AuthProvider>
          </TooltipProvider>
        </TooltipPrimitive.Provider>
      </QueryClientProvider>
    </Router>
  );
};

export default App;
